import { motion } from 'framer-motion';
import { Video, Clock, ChevronRight, CheckCircle, Hourglass } from 'lucide-react';
import VerdictBadge from './VerdictBadge';

/**
 * SimSessionHistoryItem — for interview simulation sessions
 *
 * Props:
 *  session — { session_id, difficulty, language, status, created_at, submitted_at,
 *              total_score, question_count,
 *              schema_version, ccri_total, ccri_band, ars_total, verdict }
 *  onClick — function
 *  t       — translation function
 *  lang    — 'tr' | 'en'
 */

export default function SimSessionHistoryItem({ session, onClick, t, lang }) {
  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Intl.DateTimeFormat(lang === 'tr' ? 'tr-TR' : 'en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const isScored = session.status === 'scored';
  const isPending = session.status === 'pending_review';

  const statusLabel = isScored
    ? t('interview.sim.status_scored')
    : isPending
      ? t('interview.sim.status_pending')
      : t('cabin.interview.history.status_active');

  const statusColor = isScored
    ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
    : isPending
      ? 'bg-amber-50 text-amber-600 border-amber-200'
      : 'bg-gray-50 text-gray-500 border-gray-200';

  const StatusIcon = isScored ? CheckCircle : Hourglass;

  const maxScore = session.max_score ?? (session.question_count ? session.question_count * 10 : 50);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.01, x: 2 }}
      whileTap={{ scale: 0.99 }}
      className="w-full text-left bg-white border-2 border-gray-200 rounded-2xl p-4 flex items-center gap-4 hover:border-[var(--brand-primary)] hover:shadow-[2px_2px_0px_0px_var(--brand-primary)] transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] min-h-[44px]"
    >
      {/* Icon */}
      <div className="shrink-0 w-10 h-10 rounded-xl bg-[var(--surface-soft)] border-2 border-[color-mix(in_srgb,var(--brand-primary)_30%,white)] flex items-center justify-center">
        <Video size={18} className="text-[var(--brand-primary)]" aria-hidden="true" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          {/* Status badge */}
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${statusColor}`}>
            <StatusIcon size={11} aria-hidden="true" />
            {statusLabel}
          </span>
          {/* Difficulty */}
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
            {t('cabin.interview.hub.difficulty')} {session.difficulty}
          </span>
          {/* Language */}
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[var(--surface-soft)] text-[var(--brand-primary)] border border-[color-mix(in_srgb,var(--brand-primary)_25%,white)]">
            {(session.language || 'tr').toUpperCase()}
          </span>
        </div>
        <p className="text-xs text-gray-500 truncate flex items-center gap-1">
          <Clock size={11} aria-hidden="true" />
          {formatDate(session.submitted_at || session.created_at)}
        </p>
      </div>

      {/* Score — v3 sessions show CCRI+verdict; others show raw total */}
      {isScored && session.schema_version === 3 && session.ccri_total != null ? (
        <div className="shrink-0 text-right flex flex-col items-end gap-1">
          <span
            className="text-xl font-black text-[var(--brand-primary)] tabular-nums"
            aria-label={`CCRI ${session.ccri_total}`}
          >
            {Math.round(session.ccri_total)}
          </span>
          <p className="text-[10px] text-gray-400 font-semibold">CCRI</p>
          {session.verdict && <VerdictBadge verdict={session.verdict} t={t} />}
        </div>
      ) : isScored && session.total_score != null ? (
        <div className="shrink-0 text-right">
          <span className="text-xl font-black text-emerald-600">{session.total_score}</span>
          <p className="text-xs text-gray-400">/{maxScore}</p>
        </div>
      ) : null}

      <ChevronRight size={16} className="text-gray-400 shrink-0" aria-hidden="true" />
    </motion.button>
  );
}
